import type { TableRowData } from './tableUtils';

function coerceTrimmedString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed !== '' ? trimmed : undefined;
}

function toDateKey(value: unknown): string | undefined {
  const raw = coerceTrimmedString(value);
  if (!raw) return undefined;
  const match = raw.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : raw;
}

function toValueLabel(value: unknown, unit: string | undefined): string | undefined {
  const raw = value === null || value === undefined ? undefined : String(value).trim();
  if (!raw) return undefined;
  return unit ? `${raw} ${unit}` : raw;
}

export type PivotByDateKeyOptions = {
  nameKey: string;
  unitKey?: string;
  startDateKey: string;
  valueKey: string;
  valueSeparator?: string;
  idPrefix?: string;
};

/**
 * Pivot row data into a wide table keyed by a derived date key (e.g. YYYY-MM-DD).
 *
 * Intended for vitals-like datasets:
 * - Each input row has a "name" (e.g. Blood Pressure), optional "unit", a start date, and a value.
 * - Output groups by (name, unit) and creates a column per dateKey with a value label.
 */
export function pivotByDateKey(
  rows: readonly object[],
  {
    nameKey,
    unitKey,
    startDateKey,
    valueKey,
    valueSeparator = ' | ',
    idPrefix = 'pivot',
  }: PivotByDateKeyOptions
): TableRowData[] {
  const byKey = new Map<string, TableRowData>();

  for (const row of rows) {
    const record = row as TableRowData;
    const name = coerceTrimmedString(record[nameKey]);
    if (!name) continue;

    const unit = unitKey ? coerceTrimmedString(record[unitKey]) : undefined;
    const dateKey = toDateKey(record[startDateKey]);
    if (!dateKey) continue;

    const valueLabel = toValueLabel(record[valueKey], unit);
    if (!valueLabel) continue;

    const groupKey = unit ? `${name}||${unit}` : name;
    const existing = byKey.get(groupKey);
    const pivotRow: TableRowData =
      existing ??
      ({
        id: `${idPrefix}:${groupKey}`,
        [nameKey]: name,
        ...(unit && unitKey ? { [unitKey]: unit } : {}),
      } satisfies TableRowData);

    const previous = pivotRow[dateKey];
    if (typeof previous === 'string' && previous.trim() !== '' && previous !== valueLabel) {
      pivotRow[dateKey] = `${previous}${valueSeparator}${valueLabel}`;
    } else {
      pivotRow[dateKey] = valueLabel;
    }

    if (!existing) {
      byKey.set(groupKey, pivotRow);
    }
  }

  const result = Array.from(byKey.values());
  result.sort((a, b) => {
    const aName = typeof a[nameKey] === 'string' ? a[nameKey] : '';
    const bName = typeof b[nameKey] === 'string' ? b[nameKey] : '';
    return aName.localeCompare(bName);
  });

  return result;
}
